/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { handleError } from '@slr/shared-library';
import {
  ChangeOrganizationRequest,
  CreateOrganizationRequest,
  DeleteOrganizationRequest,
  GetAllOrganizationsRequest,
  GetOrganizationRequest,
  GetOrganizationsOfCurrentPersonRequest,
  OrganizationsApi
} from '@slr/user-service-external-sdk';
import { useCallback, useMemo } from 'react';
import { useGetUserServiceApiConfiguration } from './user.service';

export const useOrganizationsApi = () => {
  const configuration = useGetUserServiceApiConfiguration();
  const apiInstance = useMemo(
    () => new OrganizationsApi(configuration),
    [configuration]
  );

  const getOrganization = useCallback(
    (requestParameters: GetOrganizationRequest) =>
      apiInstance.getOrganization(requestParameters).catch(handleError),
    [apiInstance]
  );

  const getOrganizationStrict = useCallback(
    (requestParameters: GetOrganizationRequest) =>
      apiInstance.getOrganization(requestParameters),
    [apiInstance]
  );

  const getOrganizationsOfCurrentPerson = useCallback(
    (requestParameters: GetOrganizationsOfCurrentPersonRequest) =>
      apiInstance
        .getOrganizationsOfCurrentPerson(requestParameters)
        .catch(handleError),
    [apiInstance]
  );

  const createOrganization = useCallback(
    (requestParameters: CreateOrganizationRequest) =>
      apiInstance.createOrganization(requestParameters).catch(handleError),
    [apiInstance]
  );

  const createOrganizationStrict = useCallback(
    (requestParameters: CreateOrganizationRequest) =>
      apiInstance.createOrganization(requestParameters),
    [apiInstance]
  );

  const changeOrganization = useCallback(
    (requestParameters: ChangeOrganizationRequest) =>
      apiInstance.changeOrganization(requestParameters).catch(handleError),
    [apiInstance]
  );

  const changeOrganizationStrict = useCallback(
    (requestParameters: ChangeOrganizationRequest) =>
      apiInstance.changeOrganization(requestParameters),
    [apiInstance]
  );

  const deleteOrganization = useCallback(
    (requestParameters: DeleteOrganizationRequest) =>
      apiInstance.deleteOrganization(requestParameters).catch(handleError),
    [apiInstance]
  );

  const deleteOrganizationStrict = useCallback(
    (requestParameters: DeleteOrganizationRequest) =>
      apiInstance.deleteOrganization(requestParameters),
    [apiInstance]
  );

  const getAllOrganizations = useCallback(
    (requestParameters: GetAllOrganizationsRequest) =>
      apiInstance.getAllOrganizations(requestParameters).catch(handleError),
    [apiInstance]
  );

  return {
    getOrganization,
    getOrganizationStrict,
    getOrganizationsOfCurrentPerson,
    createOrganization,
    createOrganizationStrict,
    changeOrganization,
    changeOrganizationStrict,
    deleteOrganization,
    deleteOrganizationStrict,
    getAllOrganizations
  };
};
